import { Component, Input, OnChanges, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription, forkJoin } from 'rxjs';
import { AuthService } from '../../services/auth/auth.service';
import { BusinessService } from '../../services/business.service';
import { UserRoleService } from '../../services/user-role.service';
import { UserSummary } from '../../models/user.model';

@Component({
  selector: 'app-website-builders', standalone: true, imports: [CommonModule, FormsModule],
  template: `
    <section class="card" *ngIf="auth.can('Businesses.Membership.Manage')">
      <h3>Website builders</h3><p>Assignment gives a WebsiteBuilder access to this business's website. No business role is added.</p>
      <p *ngIf="loading" role="status">Loading builders…</p>
      <p *ngIf="error" class="note note-err" role="alert">{{ error }}</p>
      <button *ngIf="error" class="act-ghost" (click)="load()" [disabled]="busy">Retry</button>
      <ul><li *ngFor="let user of assigned">{{ user.firstName }} {{ user.lastName }} ({{ user.email }})
        <button class="act-ghost" *ngIf="auth.canChangeAccount(user, true)" (click)="remove(user)" [disabled]="busy || loading">Remove assignment</button>
      </li></ul>
      <p *ngIf="!loading && !error && !assigned.length">No website builders assigned.</p>
      <label>Website builder <select class="inp" [(ngModel)]="userId" [disabled]="busy || loading || !!error">
        <option value="">Select a builder…</option>
        <option *ngFor="let user of available" [value]="user.userId">{{ user.firstName }} {{ user.lastName }} ({{ user.email }})</option>
      </select></label>
      <button class="act" (click)="assign()" [disabled]="!userId || busy || loading || !!error">Assign builder</button>
    </section>`
})
export class WebsiteBuildersComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) businessId = '';
  readonly auth = inject(AuthService);
  private readonly users = inject(UserRoleService);
  private readonly businesses = inject(BusinessService);
  private request?: Subscription;
  assigned: UserSummary[] = [];
  available: UserSummary[] = [];
  userId = ''; loading = false; busy = false; error = '';
  ngOnChanges(): void { this.request?.unsubscribe(); this.busy = false; this.load(); }
  ngOnDestroy(): void { this.request?.unsubscribe(); }
  load(): void {
    if (!this.businessId || !this.auth.can('Businesses.Membership.Manage')) return;
    this.loading = true; this.error = ''; this.userId = ''; this.assigned = []; this.available = [];
    this.request = forkJoin({ users: this.users.getUsers(), members: this.businesses.getBusinessUsers(this.businessId) }).subscribe({
      next: ({users, members}) => {
        const ids = new Set(members.map(m => m.userId.toLowerCase()));
        const builders = users.filter(u => u.roles.some(r => r.roleName.toLowerCase() === 'websitebuilder'));
        this.assigned = builders.filter(u => ids.has(u.userId.toLowerCase()));
        this.available = builders.filter(u => !ids.has(u.userId.toLowerCase()) && this.auth.canChangeAccount(u, true));
        this.loading = false;
      },
      error: () => { this.loading = false; this.error = 'Could not load website builder assignments.'; }
    });
  }
  assign(): void {
    const user = this.available.find(u => u.userId === this.userId);
    if (!user || this.busy || !this.auth.can('Businesses.Membership.Manage') || !this.auth.canChangeAccount(user, true)) return;
    if (!window.confirm(`Assign ${user.email} to build and publish this business's website?`)) return;
    this.busy = true;
    this.request = this.users.assignWebsiteBuilder(user.userId, this.businessId).subscribe({
      next: () => { this.busy = false; this.load(); },
      error: () => { this.busy = false; this.error = 'Assignment failed. No access was granted.'; }
    });
  }
  remove(user: UserSummary): void {
    if (this.busy || !this.auth.can('Businesses.Membership.Manage') || !this.auth.canChangeAccount(user, true)) return;
    if (!window.confirm(`Remove ${user.email} from this business? This removes their business membership and website access.`)) return;
    this.busy = true;
    this.request = this.users.removeUserFromBusiness(user.userId, this.businessId).subscribe({
      next: () => { this.busy = false; this.load(); },
      error: () => { this.busy = false; this.error = 'Could not remove the assignment. Access may still be active.'; }
    });
  }
}
